import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { styles } from './pm-page.style.js';
import { getPMWorkOrdersByCompany, getAssetsByCompany, getUsersByCompany, updateWorkOrder } from '@cmms/api';
import { db } from '../../services/auth.service.js';
import { WorkOrder } from '../../../types/work-order.types.js';
import { createWorkOrdersTableConfig } from '../../work-orders/work-orders-page/work-orders-table-config.js';
import { SubscriptionPlanCat } from '@cmms/models';
import '../../work-orders/work-order-viewer/work-order-viewer.js';
import '../../common/upgrade-placeholder/upgrade-placeholder.js';
import '../../common/upgrade-modal/upgrade-modal.js';

@customElement('pm-page')
export class PMPage extends LitElement {
  constructor() {
    super();
  }
  static styles = styles;
  @property({ type: String })
  companyId = ""

  @property({ type: Number })
  subscriptionPlan: SubscriptionPlanCat = SubscriptionPlanCat.STARTER;

  @state() private pmWorkOrders: WorkOrder[] = [];
  @state() private showUpgradeModal = false;
  @state() private upgradeContext = { action: '', feature: '' };
  @state() private assets: any[] = [];
  @state() private users: any[] = [];
  @state() private isLoading = true;
  @state() private error: string | null = null;
  @state() private currentView: 'list' | 'calendar' = 'list';
  @state() private selectedWorkOrder: WorkOrder | null = null;
  @state() private sortColumn: string = '';
  @state() private sortDirection: 'asc' | 'desc' = 'asc';
  private cachedTableConfig: any = null;

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
    // Listen for direct open requests (from dashboard)
    window.addEventListener('open-pm-from-dashboard', this.handleOpenPMEvent as EventListener);
  }

  updated(changedProperties: Map<string, any>) {
    super.updated(changedProperties);
    if (changedProperties.has('companyId')) {
      this.loadData();
    }
  }

  private hasPMAccess(): boolean {
    // Professional (2) and Enterprise (3) have access, Starter (1) does not
    return this.subscriptionPlan >= SubscriptionPlanCat.PROFESSIONAL;
  }

  private handleUpgradeRequest(event: CustomEvent) {
    this.upgradeContext = {
      action: event.detail.action || 'Access',
      feature: 'Preventive Maintenance'
    };
    this.showUpgradeModal = true;
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.removeEventListener('open-pm-from-dashboard', this.handleOpenPMEvent as EventListener);
  }

  private asArray<T = any>(resp: unknown): T[] {
    if (Array.isArray(resp)) {
      return resp as T[];
    }
    
    if (resp && typeof resp === 'object' && Array.isArray((resp as any).data)) {
      return (resp as any).data as T[];
    }
    
    return [];
  }

  private async loadData() {
    try {
      this.isLoading = true;
      this.error = null;
      
      const [pmWorkOrdersResult, assetsResult, usersResult] = await Promise.all([
        getPMWorkOrdersByCompany(db, this.companyId),
        getAssetsByCompany(db, this.companyId),
        getUsersByCompany(db, this.companyId)
      ]);

      // Get PM work orders directly from the API (no filtering needed)
      this.pmWorkOrders = this.asArray<WorkOrder>(pmWorkOrdersResult);
      
      this.assets = this.asArray<any>(assetsResult);
      this.users = this.asArray<any>(usersResult);

      console.log(`Loaded ${this.pmWorkOrders.length} PM work orders`);
    } catch (error: any) {
      this.error = error?.message ?? 'Failed to load PM work orders';
    } finally {
      this.isLoading = false;
    }
  }

  private getRequestedOpenId(): string | null {
    try {
      const id = localStorage.getItem('pm_open_id');
      if (id) {
        localStorage.removeItem('pm_open_id');
        return id;
      }
    } catch {}
    return null;
  }

  private handleOpenPMEvent = (e: CustomEvent) => {
    const id = (e.detail && e.detail.pmId) as string | undefined;
    if (!id) return;
    const workOrder = this.pmWorkOrders.find(wo => wo.id === id);
    if (workOrder) {
      this.selectedWorkOrder = workOrder;
    }
  };

  private handleRowClick = (workOrder: WorkOrder) => {
    this.selectedWorkOrder = workOrder;
  };

  private handleDeleteClick = (workOrder: WorkOrder) => {
    // TODO: Implement delete functionality for work orders
    console.log('Delete work order:', workOrder);
  };


  private handleFilterChange = (filters: Record<string, any>) => {
    // TODO: Implement filter functionality
  };

  private handleSort = (column: string, direction: 'asc' | 'desc') => {
    this.sortColumn = column;
    this.sortDirection = direction;
    
    // Sort the pmWorkOrders array
    this.pmWorkOrders = [...this.pmWorkOrders].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      // Handle nested properties and special cases
      switch (column) {
        case 'description':
          aValue = (a as any).model?.description || '';
          bValue = (b as any).model?.description || '';
          break;
        case 'asset_id':
          // Find asset name for sorting
          const assetA = this.assets.find(asset => asset.id === (a as any).model?.asset_id);
          const assetB = this.assets.find(asset => asset.id === (b as any).model?.asset_id);
          aValue = (assetA as any)?.model?.name || '';
          bValue = (assetB as any)?.model?.name || '';
          break;
        case 'assigned_to':
          aValue = (a as any).model?.assigned_to || '';
          bValue = (b as any).model?.assigned_to || '';
          break;
        case 'priority_cat':
          aValue = (a as any).model?.priority_cat || 0;
          bValue = (b as any).model?.priority_cat || 0;
          break;
        case 'status_cat':
          aValue = (a as any).model?.status_cat || 0;
          bValue = (b as any).model?.status_cat || 0;
          break;
        case 'created_at':
          aValue = new Date((a as any).model?.created_at || 0).getTime();
          bValue = new Date((b as any).model?.created_at || 0).getTime();
          break;
        case 'pm_due_at':
          aValue = new Date((a as any).model?.pm_due_at || 0).getTime();
          bValue = new Date((b as any).model?.pm_due_at || 0).getTime();
          break;
        case 'active':
          aValue = (a as any).model?.active ? 1 : 0;
          bValue = (b as any).model?.active ? 1 : 0;
          break;
        default:
          aValue = '';
          bValue = '';
      }
      
      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return direction === 'asc' ? comparison : -comparison;
      }
      
      // Handle numeric comparison
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle date comparison
      if (aValue instanceof Date && bValue instanceof Date) {
        const comparison = aValue.getTime() - bValue.getTime();
        return direction === 'asc' ? comparison : -comparison;
      }
      
      // Fallback comparison
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };




  private handleWorkOrderViewerClosed() {
    this.selectedWorkOrder = null;
  }

  private async handleWorkOrderUpdated() {
    // Reload data when work order is updated
    await this.loadData();
  }


  private handleCalendarEventClick(event: CustomEvent) {
    const { workOrderId, title, dueDate, isOverdue } = event.detail;
    
    // Find the PM work order and show details
    const workOrder = this.pmWorkOrders.find(wo => wo.id === workOrderId);
    if (workOrder) {
      this.selectedWorkOrder = workOrder;
    }
  }

  private async handleCalendarEventMoved(event: CustomEvent) {
    const { workOrderId, newPmDueAt, oldPmDueAt } = event.detail;
    
    console.log('Updating PM work order due date:', {
      workOrderId,
      newPmDueAt,
      oldPmDueAt
    });
    
    try {
      // Update the work order with the new pm_due_at
      await updateWorkOrder(db, workOrderId, {
        pm_due_at: newPmDueAt,
        updated_at: Date.now()
      });
      
      // Show success message
      const toast = document.createElement('sl-alert') as any;
      toast.variant = 'success';
      toast.closable = true;
      toast.innerHTML = `
        <sl-icon slot="icon" name="check-circle"></sl-icon>
        <strong>PM Due Date Updated!</strong><br>
        The preventive maintenance due date has been updated.
      `;
      document.body.appendChild(toast);
      toast.toast();
      
      // Reload the data to reflect the changes
      await this.loadData();
      
    } catch (error) {
      console.error('Error updating PM work order due date:', error);
      
      // Show error message
      const toast = document.createElement('sl-alert') as any;
      toast.variant = 'danger';
      toast.closable = true;
      toast.innerHTML = `
        <sl-icon slot="icon" name="exclamation-octagon"></sl-icon>
        <strong>Update Failed!</strong><br>
        Could not update the PM due date. Please try again.
      `;
      document.body.appendChild(toast);
      toast.toast();
      
      // Reload data to revert any visual changes
      await this.loadData();
    }
  }

  private async handleCalendarEventResized(event: CustomEvent) {
    // Handle event resizing if needed (typically not used for PM work orders)
    console.log('PM work order resized:', event.detail);
  }


  render() {
    // Only recreate table config if data has changed
    if (!this.cachedTableConfig || 
        this.cachedTableConfig.data !== this.pmWorkOrders ||
        this.cachedTableConfig.loading !== this.isLoading ||
        this.cachedTableConfig.error !== this.error ||
        this.cachedTableConfig.sortColumn !== this.sortColumn ||
        this.cachedTableConfig.sortDirection !== this.sortDirection) {
      
      this.cachedTableConfig = createWorkOrdersTableConfig(
        this.pmWorkOrders,
        this.assets,
        this.users,
        {
          onRowClick: this.handleRowClick,
          onDelete: this.handleDeleteClick,
          onFilterChange: this.handleFilterChange,
          onSort: this.handleSort
        }
      );

      // Override loading and error state
      this.cachedTableConfig.loading = this.isLoading;
      this.cachedTableConfig.error = this.error;
    }

    const tableConfig = this.cachedTableConfig;

    // Show upgrade placeholder for Basic plan users
    if (!this.hasPMAccess()) {
      return html`
        <div class="pm-page">
          <upgrade-placeholder
            featureName="Preventive Maintenance"
            description="Prevent equipment failures before they happen with our comprehensive preventive maintenance system. Schedule recurring tasks, track compliance, and extend asset life."
            icon="calendar-check"
            .currentPlan=${this.subscriptionPlan}
            .benefits=${[
              'Automated PM scheduling and notifications',
              'Equipment maintenance history tracking',
              'Compliance reporting and documentation',
              'Mobile-friendly maintenance checklists',
              'Integration with work orders and assets',
              'Predictive maintenance analytics'
            ]}
            @upgrade-requested=${this.handleUpgradeRequest}
          ></upgrade-placeholder>

          <!-- Upgrade Modal -->
          <upgrade-modal
            ?open=${this.showUpgradeModal}
            featureName=${this.upgradeContext.feature}
            actionName=${this.upgradeContext.action}
            @upgrade-confirmed=${this.handleUpgradeRequest}
            @sl-request-close=${() => this.showUpgradeModal = false}
          ></upgrade-modal>
        </div>
      `;
    }

    return html`
      <div class="pm-page ${this.currentView === 'calendar' ? 'calendar-mode' : ''}">
        ${this.currentView === 'calendar' ? html`
          <!-- Calendar view with full control -->
          <div class="calendar-full-view">
            <div class="calendar-header">
              <div class="view-toggle">
                <sl-button-group>
                  <sl-button
                    variant=${(this.currentView as string) === 'list' ? 'primary' : 'default'}
                    @click=${() => this.currentView = 'list'}
                  >
                    <sl-icon name="list" slot="prefix"></sl-icon>
                    List View
                  </sl-button>
                  <sl-button
                    variant=${(this.currentView as string) === 'calendar' ? 'primary' : 'default'}
                    @click=${() => this.currentView = 'calendar'}
                  >
                    <sl-icon name="calendar" slot="prefix"></sl-icon>
                    Calendar View
                  </sl-button>
                </sl-button-group>
              </div>
            </div>
            <div class="calendar-container">
              <pm-calendar 
                .companyId=${this.companyId}
                .pmWorkOrders=${this.pmWorkOrders}
                @pm-event-click=${this.handleCalendarEventClick}
                @pm-event-moved=${this.handleCalendarEventMoved}
                @pm-event-resized=${this.handleCalendarEventResized}
              ></pm-calendar>
            </div>
          </div>
        ` : html`
          <!-- Table view with normal structure -->
          <div class="page-header">
            <div class="header-actions">
              <div class="view-toggle">
                <sl-button-group>
                  <sl-button
                    variant=${(this.currentView as string) === 'list' ? 'primary' : 'default'}
                    @click=${() => this.currentView = 'list'}
                  >
                    <sl-icon name="list" slot="prefix"></sl-icon>
                    List View
                  </sl-button>
                  <sl-button
                    variant=${(this.currentView as string) === 'calendar' ? 'primary' : 'default'}
                    @click=${() => this.currentView = 'calendar'}
                  >
                    <sl-icon name="calendar" slot="prefix"></sl-icon>
                    Calendar View
                  </sl-button>
                </sl-button-group>
              </div>
            </div>
          </div>
          <data-table .config=${tableConfig}></data-table>
        `}

        <!-- Work Order Viewer -->
        <work-order-viewer
          .open=${!!this.selectedWorkOrder}
          .workOrder=${this.selectedWorkOrder}
          .companyId=${this.companyId}
          .firestore=${db}
          @modal-closed=${this.handleWorkOrderViewerClosed}
          @work-order-updated=${this.handleWorkOrderUpdated}>
        </work-order-viewer>
      </div>
    `;
  }


}
